import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import configData from '../config.json';
import { useNotificationContext } from '../context/notification';
import { useOrderContext } from '../context/order';
import { useStampWallet } from '../context/stampWallet';

import { isPendingOrderForVenue } from '../utils/pendingOrder';
import { hasWoltDeliveryUpdate, isWoltDeliveryFailed } from '../utils/woltDelivery';
import {
  announceDeliveryChange,
  deliverySeedOf,
  handOverDelivery,
  handOverShows,
  isDeliveryOrder,
  isFollowedDelivery,
} from '../utils/deliveryTracking';
import {
  refundNotice,
  refundNoticeKey,
  woltFailurePushType,
  woltNoticeKey,
  woltPushNotice,
} from '../utils/woltPush';

const STORAGE_KEY = 'hw_pending_orderv2';
const POLL_INTERVAL_MS = 5000;
// Grace re-pull for the stampcard wallet after a completion (see
// refreshStampsAfterCompletion) — the award lands just after the status flip.
const STAMP_RECHECK_DELAY_MS = 2500;

// The delivery failed — the venue cancelled it from the admin dashboard
// ("Annuller Wolt", woltDeliveryCancelledAt), or Wolt rejected, cancelled or
// gave up on it (woltDeliveryStatus), or the backend says so outright
// (woltDeliveryState: 'failed'). Only the courier is gone: the FOOD order is
// still live and can yet be completed or refunded, so it keeps being polled.
// See utils/woltDelivery.isWoltDeliveryFailed.

// The cache is written by every checkout flow (Payment.js, the pages'
// saveOrderInfoToCache, the order context), so it is the source of truth
// for the pending order. Read it defensively — it can hold corrupt JSON.
const readCachedOrder = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const useOrderStatusPolling = (userProfile) => {
  let pathname = window.location.pathname;
  const page = pathname.split('/')?.[1];
  const restaurantId = pathname.split('/')?.[2];
  const fromKiosk = page?.includes('kiosk') || false;

  const notificationContext = useNotificationContext();
  const { pendingOrder, lastOrderId, setIsGuest, guestId } = useOrderContext();
  const navigate = useNavigate();

  const [orderStatus, setOrderStatus] = useState({
    isPending: false,
    isConfirmed: false,
    currentOrder: null,
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const [payConfirm, setPayConfirm] = useState({});
  const [isProcessingOrderModalOpen, setIsProcessingOrderModalOpen] = useState(false);
  // Read by the poll and push callbacks, which outlive the render they were made in.
  const processingModalOpenRef = useRef(false);
  processingModalOpenRef.current = isProcessingOrderModalOpen;
  const handedOverOrderIdRef = useRef(null);

  const pendingPollRef = useRef(null);
  const confirmedPollRef = useRef(null);
  // Which order the active interval belongs to, so polling retargets when a
  // new order is placed instead of silently keeping the old interval alive.
  const polledOrderIdRef = useRef(null);
  // Holds the generation of the request currently in flight (null when idle),
  // so slow responses don't stack up extra checkstatus calls and a stale
  // response can't clear the guard for a newer request.
  const inFlightRef = useRef(null);
  // Bumped whenever polling stops or retargets; a response that comes back
  // for an older generation is discarded instead of overwriting newer state.
  const pollGenerationRef = useRef(0);
  // Order already adopted by the init effect (modal opened / navigated once),
  // so effect re-runs don't re-navigate or re-open the modal.
  const adoptedOrderIdRef = useRef(null);
  const checkedLastOrderIdRef = useRef(null);

  // Stampcard V2: stamps are credited server-side by `restaurantcompleteorder`
  // (it awaits awardStampsForOrder when the POS marks the order Completed), so
  // the instant this poll reports `order_completed` the cached wallet and the
  // menu's progress pills are stale. Without this refresh the new stamp only
  // appears after a page reload — the mount effect was the ONLY thing pulling it.
  //
  // useStampWallet() is undefined when no StampWalletWrapper is mounted, and the
  // context recreates its actions every render, so it is held in a ref: the poll
  // callbacks below must keep stable identities or every wallet render would
  // churn them (and stopping/retargeting polling with them).
  const { refreshStamps } = useStampWallet() || {};
  const refreshStampsRef = useRef(null);
  refreshStampsRef.current = refreshStamps;
  // Same reason: the interval closure is created once per order, but the
  // customer can move to another venue's menu before it completes.
  const restaurantIdRef = useRef(restaurantId);
  restaurantIdRef.current = restaurantId;
  const stampRecheckTimerRef = useRef(null);
  const stampRefreshedOrderIdRef = useRef(null);

  const refreshStampsAfterCompletion = useCallback((orderId) => {
    // One completion, one refresh: both pollers can observe `order_completed`
    // for the same order (a pending-phase poll can jump straight to completed).
    if (orderId && stampRefreshedOrderIdRef.current === orderId) return;
    stampRefreshedOrderIdRef.current = orderId || null;

    const run = () => {
      const refresh = refreshStampsRef.current;
      if (typeof refresh !== 'function') return;
      // The context swallows its own errors; guard anyway so a rejected promise
      // can never surface as an unhandled rejection inside the poll interval.
      Promise.resolve(refresh(restaurantIdRef.current)).catch(() => {});
    };

    run();

    // The POS route saves Status='completed' BEFORE it awards the stamps, so a
    // poll landing in that window sees the completion against a wallet that has
    // not been credited yet. One short re-pull closes that gap; it is cheap
    // because it happens once per completed order.
    if (stampRecheckTimerRef.current) clearTimeout(stampRecheckTimerRef.current);
    stampRecheckTimerRef.current = setTimeout(() => {
      stampRecheckTimerRef.current = null;
      run();
    }, STAMP_RECHECK_DELAY_MS);
  }, []);

  // Check order status from API
  const checkOrderStatus = useCallback(
    async (orderId) => {
      if (!orderId) return null;
      try {
        const response = await axios({
          method: 'GET',
          url: `${configData.SERVER_URL}/orders/checkstatus?id=${orderId}`,
          headers: {
            Authorization: `Bearer ${userProfile?.token || ''}`,
          },
        });

        return response.data;
      } catch (error) {
        console.error('Error checking order status:', error);
        return null;
      }
    },
    [userProfile],
  );

  // Update order cache. Merges over the cached copy (it may have been
  // refreshed since polling started) so a stale snapshot never overwrites
  // newer data; if the cache already holds a different order, it is left alone.
  const updateOrderCache = useCallback((order, status) => {
    const cached = readCachedOrder();
    if (cached?._id && order?._id && cached._id !== order._id) {
      return { ...order, status };
    }
    const updatedOrder = { ...order, ...(cached || {}), status };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedOrder));
    return updatedOrder;
  }, []);

  // Clear order cache. When an orderId is given, only clears the cache if it
  // still belongs to that order — never wipes a newer order's data.
  const clearOrderCache = useCallback((orderId) => {
    if (orderId) {
      const cached = readCachedOrder();
      if (cached?._id && cached._id !== orderId) return;
    }
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Stop polling functions
  const stopPendingPolling = useCallback(() => {
    pollGenerationRef.current += 1;
    if (pendingPollRef.current) {
      clearInterval(pendingPollRef.current);
      pendingPollRef.current = null;
    }
  }, []);

  const stopConfirmedPolling = useCallback(() => {
    pollGenerationRef.current += 1;
    if (confirmedPollRef.current) {
      clearInterval(confirmedPollRef.current);
      confirmedPollRef.current = null;
    }
  }, []);

  // The courier is gone and the tracking link is dead, but the order is not:
  // merge the status in (the modal and the order view then show the failure
  // instead of an ETA) and tell the guest once — in the words of the push this
  // failure sends, which is then not shown again. Polling carries on, so a
  // completion or a cancellation of the order itself still reaches them.
  const handleWoltDeliveryFailed = useCallback(
    (orderId, status) => {
      setPayConfirm((prev) => ({ ...prev, order: status }));

      const type = woltFailurePushType(status);
      notificationContext?.notifyOnce?.(woltNoticeKey(type, orderId), woltPushNotice(type, orderId));
    },
    [notificationContext],
  );

  // The order is over and the guest's money is back — a held Wolt order the
  // courier booking refused. Stop polling and drop the cache, but keep the
  // modal open: it now says the order was cancelled and refunded, and it can
  // be closed (a pending order's modal cannot). Told once: in that modal when
  // it is open, otherwise as a notice (the app's refund banner).
  const handleOrderRefunded = useCallback(
    (orderId, status) => {
      clearOrderCache(orderId);
      stopPendingPolling();
      stopConfirmedPolling();
      setOrderStatus((prev) => ({
        ...prev,
        isPending: false,
        isConfirmed: false,
        isRefunded: true,
        currentOrder: null,
      }));
      setPayConfirm((prev) => ({ ...prev, order: status }));
      notificationContext?.notifyOnce?.(
        refundNoticeKey(orderId),
        processingModalOpenRef.current || fromKiosk ? null : refundNotice(status?.reason, orderId),
      );
    },
    [clearOrderCache, stopPendingPolling, stopConfirmedPolling, notificationContext, fromKiosk],
  );

  // A delivery the venue has just accepted is the floating tracker's to follow
  // (components/DeliveryTracker): ProcessingOrderModal, which covered the wait,
  // makes way, and the tracker opens on the delivery if the modal was on
  // screen. Not on the kiosk, not for one placed signed in by someone since
  // signed out, and not unless the tracker will show it. Once per order; says
  // whether it happened.
  const handOverIfDelivery = useCallback(
    (order, status) => {
      if (fromKiosk || !order?._id || !isDeliveryOrder(order)) return false;
      if (handedOverOrderIdRef.current === order._id) return true;
      const signedIn = Boolean(userProfile?.token);
      if (order.placedSignedIn && !signedIn) return false;
      const seed = deliverySeedOf(order, { signedIn });
      if (!handOverShows(seed, status)) return false;
      handedOverOrderIdRef.current = order._id;

      const open = processingModalOpenRef.current;
      setIsProcessingOrderModalOpen(false);
      handOverDelivery(seed, status, { open });
      return true;
    },
    [fromKiosk, userProfile],
  );

  // Start polling for confirmed order
  const startConfirmedOrderPolling = useCallback(
    (order) => {
      if (!order?._id) return;
      // Already polling this exact order — nothing to do.
      if (confirmedPollRef.current && polledOrderIdRef.current === order._id) return;

      // A different order (or a pending-phase poller) is active — retarget.
      stopPendingPolling();
      stopConfirmedPolling();
      polledOrderIdRef.current = order._id;
      inFlightRef.current = null;

      setOrderStatus((prev) => ({
        ...prev,
        isPending: false,
        isConfirmed: true,
        isRefunded: false,
        currentOrder: order,
      }));

      confirmedPollRef.current = setInterval(async () => {
        if (inFlightRef.current !== null) return;
        const generation = pollGenerationRef.current;
        inFlightRef.current = generation;
        try {
          const status = await checkOrderStatus(order._id);
          if (pollGenerationRef.current !== generation) return;

          if (status?.type === 'order_completed') {
            clearOrderCache(order._id);
            setOrderStatus((prev) => ({
              ...prev,
              isPending: false,
              isConfirmed: false,
              currentOrder: null,
            }));
            stopConfirmedPolling();
            // The completion is what earns the stamp — re-pull the wallet so the
            // menu's progress pills and any newly minted voucher appear now,
            // rather than on the customer's next page load.
            refreshStampsAfterCompletion(order._id);

            notificationContext?.setNotificatioModal?.({
              open: true,
              notification: {
                notification: {
                  body: status.message || 'Your order has been completed!',
                },
              },
            });
          } else if (status?.type === 'order_cancelled') {
            clearOrderCache(order._id);
            setOrderStatus((prev) => ({
              ...prev,
              isConfirmed: false,
              isPending: false,
              currentOrder: null,
            }));
            setPayConfirm({}); // Clear payConfirm
            setIsProcessingOrderModalOpen(false);
            stopConfirmedPolling();

            if (status.reason !== 'Customer initiated (web)') {
              notificationContext?.setNotificatioModal?.({
                open: true,
                notification: {
                  notification: {
                    body: status.message || status.reason || 'Your order has been cancelled.',
                  },
                  data: {
                    comment: status.comment || '',
                  },
                },
              });
            }
          } else if (status?.type === 'order_refunded') {
            handleOrderRefunded(order._id, status);
          } else if (isWoltDeliveryFailed(status)) {
            handleWoltDeliveryFailed(order._id, status);
          } else if (hasWoltDeliveryUpdate(status)) {
            //the Wolt booking lands seconds AFTER the accept response (dispatch
            //runs post-response, plus the prep-time re-quote), so the payload
            //captured at the pending->confirmed flip has no tracking url yet.
            //Keep merging like the restore path does, so the tracking button
            //and delivery ETA appear without a page reload.
            setPayConfirm((prev) => ({ ...prev, order: status }));
          }
        } finally {
          if (inFlightRef.current === generation) {
            inFlightRef.current = null;
          }
        }
      }, POLL_INTERVAL_MS);
    },
    [
      checkOrderStatus,
      clearOrderCache,
      notificationContext,
      stopPendingPolling,
      stopConfirmedPolling,
      handleWoltDeliveryFailed,
      handleOrderRefunded,
      refreshStampsAfterCompletion,
    ],
  );

  // Start polling for pending order
  const startPendingOrderPolling = useCallback(
    (order) => {
      if (!order?._id) return;
      // Already polling this exact order — nothing to do.
      if (pendingPollRef.current && polledOrderIdRef.current === order._id) return;

      // A different order (or a confirmed-phase poller) is active — retarget.
      stopPendingPolling();
      stopConfirmedPolling();
      polledOrderIdRef.current = order._id;
      inFlightRef.current = null;

      if (!order.status) {
        setOrderStatus((prev) => ({
          ...prev,
          isPending: true,
          isConfirmed: false,
          isRefunded: false,
          currentOrder: order,
        }));
        setPayConfirm(order); // Set payConfirm to match the order
      }

      pendingPollRef.current = setInterval(async () => {
        if (inFlightRef.current !== null) return;
        const generation = pollGenerationRef.current;
        inFlightRef.current = generation;
        try {
          const status = await checkOrderStatus(order._id);
          if (pollGenerationRef.current !== generation) return;

          if (status?.type === 'order_accepted') {
            const updatedOrder = updateOrderCache(order, 'confirmed');
            setOrderStatus((prev) => ({
              ...prev,
              isPending: false,
              isConfirmed: true,
              currentOrder: updatedOrder,
            }));
            setPayConfirm({ ...updatedOrder, order: status || null });
            stopPendingPolling();
            startConfirmedOrderPolling(updatedOrder);
            // Accepted is when a delivery becomes trackable: the floating
            // tracker takes this one over, or picks any other up now instead
            // of on the next tab switch.
            if (!handOverIfDelivery(updatedOrder, status)) announceDeliveryChange(order._id);

            notificationContext?.setNotificatioModal?.({
              open: true,
              notification: {
                notification: {
                  body: status.message || 'Your order has been accepted!',
                },
              },
            });
          } else if (status?.type === 'order_cancelled') {
            clearOrderCache(order._id);
            setOrderStatus((prev) => ({ ...prev, isPending: false, currentOrder: null }));
            setPayConfirm({}); // Clear payConfirm
            setIsProcessingOrderModalOpen(false);
            stopPendingPolling();

            if (status.reason !== 'Customer initiated (web)') {
              notificationContext?.setNotificatioModal?.({
                open: true,
                notification: {
                  notification: {
                    body: status.message || status.reason || 'Your order has been cancelled.',
                  },
                  data: {
                    comment: status.comment || '',
                  },
                },
              });
            }
          } else if (status?.type === 'order_refunded') {
            handleOrderRefunded(order._id, status);
          } else if (status?.type === 'order_completed') {
            clearOrderCache(order._id);
            setOrderStatus((prev) => ({
              ...prev,
              isPending: false,
              isConfirmed: false,
              currentOrder: null,
            }));
            stopPendingPolling();
            // Same as the confirmed poller: a pending order can be marked
            // completed straight from the POS without passing through here.
            refreshStampsAfterCompletion(order._id);

            notificationContext?.setNotificatioModal?.({
              open: true,
              notification: {
                notification: {
                  body: status.message || 'Your order has been completed!',
                },
              },
            });
          }
        } finally {
          if (inFlightRef.current === generation) {
            inFlightRef.current = null;
          }
        }
      }, POLL_INTERVAL_MS);
    },
    [
      checkOrderStatus,
      updateOrderCache,
      clearOrderCache,
      notificationContext,
      stopPendingPolling,
      stopConfirmedPolling,
      startConfirmedOrderPolling,
      handleOrderRefunded,
      handOverIfDelivery,
      refreshStampsAfterCompletion,
    ],
  );

  const navigateToStep = useCallback(
    (step) => {
      if (restaurantId) {
        if (fromKiosk) {
          navigate(`/restaurant-kiosk/${restaurantId}/${step}`, { replace: false });
        } else {
          navigate(`/restaurants/${restaurantId}/${step}`, { replace: false });
        }
      }
    },
    [restaurantId, fromKiosk, navigate],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPendingPolling();
      stopConfirmedPolling();
      if (stampRecheckTimerRef.current) {
        clearTimeout(stampRecheckTimerRef.current);
        stampRecheckTimerRef.current = null;
      }
    };
  }, [stopPendingPolling, stopConfirmedPolling]);

  // A refund push for the order polled here ends the wait now, as the app's
  // orderRefunded does, rather than on the next poll — and it is told here,
  // not in a second modal on top.
  const onRefundPush = notificationContext?.onRefundPush;
  useEffect(
    () =>
      onRefundPush?.((refund) => {
        if (refund.orderId !== polledOrderIdRef.current) return false;
        handleOrderRefunded(refund.orderId, { type: 'order_refunded', reason: refund.reason });
        return true;
      }),
    [onRefundPush, handleOrderRefunded],
  );

  // Initialize from pendingOrder in context or cache
  useEffect(() => {
    // THIS venue's order only. The cache holds one order for the whole app and
    // carries no venue of its own, so an order pending at restaurant A was
    // adopted here, polled, and its confirmation opened over restaurant B's
    // checkout — which sent the guest back to the menu and stopped them
    // ordering at B at all.
    //
    // A narrow window while an order resolves in minutes; a theater PRE-ORDER
    // stays pending until curtain, so one pre-order locked checkout at every
    // other venue until it was served.
    const cachedAny = readCachedOrder();
    const cached = isPendingOrderForVenue(cachedAny, restaurantId) ? cachedAny : null;
    const contextOrder =
      pendingOrder?._id && isPendingOrderForVenue(pendingOrder, restaurantId)
        ? pendingOrder
        : null;

    // When context and cache disagree (a new order was placed after the
    // context snapshot was taken), the cached copy wins — never adopt a
    // stale context order over it.
    let order = null;
    if (cached?._id) {
      order =
        contextOrder && contextOrder._id === cached._id ? { ...contextOrder, ...cached } : cached;
    } else {
      order = contextOrder;
    }

    if (order?._id) {
      setPayConfirm(order);

      if (!order.status) {
        // Navigate/open the modal only the first time this order is adopted;
        // re-running the effect must not stack history entries.
        if (adoptedOrderIdRef.current !== order._id) {
          adoptedOrderIdRef.current = order._id;
          if (!fromKiosk) {
            setIsProcessingOrderModalOpen(true);
          }
          navigateToStep('menu');
        }
        startPendingOrderPolling(order);
      } else if (order.status === 'confirmed') {
        // Reopen the modal after a reload — otherwise the customer loses the
        // order view (and its tracking button) entirely until completion. A
        // delivery handed over to the floating tracker comes back there.
        // Same one-shot guard as the pending branch.
        if (adoptedOrderIdRef.current !== order._id) {
          adoptedOrderIdRef.current = order._id;
          if (!fromKiosk && !isFollowedDelivery(order._id)) {
            setIsProcessingOrderModalOpen(true);
          }
        }
        startConfirmedOrderPolling(order);
        // Re-fetch once so payConfirm regains the status payload after a
        // reload — it carries the Wolt fields (tracking url, ETA, state).
        // A delivery that failed while the tab was closed is caught here too,
        // so the restored modal says so at once instead of on the next poll.
        checkOrderStatus(order._id).then((status) => {
          if (status?.type === 'order_refunded') {
            handleOrderRefunded(order._id, status);
          } else if (isWoltDeliveryFailed(status)) {
            handleWoltDeliveryFailed(order._id, status);
          } else if (hasWoltDeliveryUpdate(status)) {
            setPayConfirm((prev) => ({ ...prev, order: status }));
          }
        });
      }
    }

    // Also check if we should be showing a modal based on lastOrderId
    // (one-shot per id — effect re-runs must not repeat the request)
    if (
      lastOrderId &&
      checkedLastOrderIdRef.current !== lastOrderId &&
      !isProcessingOrderModalOpen
    ) {
      checkedLastOrderIdRef.current = lastOrderId;
      checkOrderStatus(lastOrderId).then((status) => {
        if (status) {
          notificationContext?.setNotificatioModal?.({
            open: true,
            notification: {
              notification: {
                body: status.message || 'Order status updated',
              },
            },
          });
        }
      });
    }
  }, [pendingOrder, lastOrderId]);

  // Cancel order function
  const cancelOrder = useCallback(async () => {
    const currentOrder = orderStatus.currentOrder || pendingOrder;
    if (!currentOrder?._id) return false;

    setIsCancelling(true);
    try {
      await axios({
        method: 'PUT',
        url: `${configData.SERVER_URL}/orders/cancel`,
        data: {
          id: currentOrder._id,
          reason: 'Customer initiated (web)',
        },
        headers: {
          Authorization: `Bearer ${userProfile?.token || ''}`,
        },
      });

      // Stop polling and clean up
      stopPendingPolling();
      stopConfirmedPolling();
      clearOrderCache(currentOrder._id);
      setOrderStatus({
        isPending: false,
        isConfirmed: false,
        currentOrder: null,
      });
      setPayConfirm({});
      setShowCancelModal(false);
      setIsProcessingOrderModalOpen(false);

      // Notify success
      notificationContext?.setNotificatioModal?.({
        open: true,
        notification: {
          notification: {
            body: 'Order cancelled successfully',
          },
        },
      });

      return true;
    } catch (error) {
      console.error('Error cancelling order:', error);

      // Notify error
      notificationContext?.setNotificatioModal?.({
        open: true,
        notification: {
          notification: {
            body: 'Failed to cancel order: ' + (error.response?.data?.message || error.message),
          },
        },
      });

      return false;
    } finally {
      setIsCancelling(false);
    }
  }, [
    orderStatus.currentOrder,
    pendingOrder,
    userProfile,
    stopPendingPolling,
    stopConfirmedPolling,
    clearOrderCache,
    notificationContext,
  ]);

  // The return includes both hook-specific state and context integration
  return {
    // Order state
    orderStatus,
    pendingOrder, // From context

    // Polling control
    startPendingOrderPolling,
    startConfirmedOrderPolling,
    stopPendingPolling,
    stopConfirmedPolling,
    checkOrderStatus,

    // Order cancellation
    cancelOrder,
    showCancelModal,
    setShowCancelModal,
    isCancelling,

    // Modal states (local to hook)
    payConfirm,
    setPayConfirm,
    isProcessingOrderModalOpen,
    setIsProcessingOrderModalOpen,

    // Order cache management
    updateOrderCache,
    clearOrderCache,
  };
};
